import "chart.js/auto";
import type { ChartData, ChartOptions, TooltipItem } from "chart.js";
import { Component } from "react";
import { Line } from 'react-chartjs-2';

// ----- Supported input formats (external contracts — do not change) -----

// Legacy single-line: [{ name, value, title? }]
type LegacyLinePoint = {
    name: string;
    value: number;
    title?: string | undefined;
};

// Named multi-line from the external API / DSL (Graph Explorer / Excel):
// [{ seriesName, values: [{ name, value, title? }] }]
type NamedLineSeries = {
    seriesName: string;
    values: LegacyLinePoint[];
};

// Quality Hub multi-line: { labels, series: [{ key, label, fill?, values: [{ value, title? }] }] }
type MultiLinePoint = {
    value: number;
    title?: string | undefined;
};

type MultiLineSeries = {
    key: string;
    label: string;
    fill?: boolean | undefined;
    values: MultiLinePoint[];
};

type MultiLineData = {
    labels: string[];
    series: MultiLineSeries[];
};

type Data = LegacyLinePoint[] | NamedLineSeries[] | MultiLineData;

// ----- Canonical format used internally for rendering -----

type NormalizedPoint = {
    value: number;
    title?: string | undefined;
};

type NormalizedSeries = {
    label: string;
    fill?: boolean | undefined;
    values: NormalizedPoint[];
};

type NormalizedData = {
    labels: string[];
    series: NormalizedSeries[];
    isMultiLine: boolean;
};

type Config = {
    isPercentage?: boolean;
    displayLegends?: boolean;
};

interface IProps {
    data: Data;
    config?: Config;
    variables?: { [key: string]: string };
}

const defaultColors: string[] = [
    "#5542F6",
    "#0093EE",
    "#00B67A",
    "#FF8A00",
    "#E53935",
    "#8E24AA",
    "#3949AB",
    "#00897B",
    "#6D4C41",
    "#546E7A",
];

class LineChartWidget extends Component<IProps> {
    // The Quality Hub format is the only object-shaped input; everything else is an array.
    private isMultiLineData(data: Data): data is MultiLineData {
        return data != null
            && !Array.isArray(data)
            && Array.isArray((data as MultiLineData).series);
    }

    // Detect the named-series format by inspecting every element rather than
    // assuming a fixed position, so a stray/foreign element can't misclassify it.
    private isNamedSeriesData(data: Data): data is NamedLineSeries[] {
        return Array.isArray(data)
            && data.length > 0
            && data.every((item) =>
                item != null
                && typeof item === "object"
                && "seriesName" in item
                && Array.isArray((item as NamedLineSeries).values));
    }

    // Convert any supported input format into the single canonical shape used for rendering.
    private normalize(): NormalizedData {
        const data = this.props.data;

        // Quality Hub format: { labels, series }
        if (this.isMultiLineData(data)) {
            return {
                labels: data.labels ?? [],
                series: (data.series ?? []).map((series) => ({
                    label: String(series.label ?? ""),
                    fill: series.fill,
                    values: (series.values ?? []).map((point) => ({
                        value: point.value,
                        title: point.title,
                    })),
                })),
                isMultiLine: true,
            };
        }

        // Named multi-line format from the external API / DSL: [{ seriesName, values }]
        if (this.isNamedSeriesData(data)) {
            const labels = data[0]?.values?.map((point) => point.name) ?? [];
            return {
                labels,
                series: data.map((series) => ({
                    label: String(series.seriesName ?? ""),
                    fill: false,
                    values: (series.values ?? []).map((point) => ({
                        value: point.value,
                        title: point.title,
                    })),
                })),
                isMultiLine: true,
            };
        }

        // Legacy single-line format: [{ name, value, title? }]
        const legacy: LegacyLinePoint[] = Array.isArray(data) ? data : [];
        return {
            labels: legacy.map((point) => point.name),
            series: [{
                label: "",
                fill: true,
                values: legacy.map((point) => ({
                    value: point.value,
                    title: point.title,
                })),
            }],
            isMultiLine: false,
        };
    }

    private getColors(): string[] {
        const customColors = this.props.variables?.customColors;
        if (customColors && customColors.trim()) {
            const parsed = customColors.split(',')
                .map((color) => color.trim())
                .filter((color) => color.length > 0);

            if (parsed.length > 0) {
                return parsed;
            }
        }

        return defaultColors;
    }

    // Series whose label matches an entry in the `dashedLines` variable are drawn
    // as dashed instead of solid. Matching is trimmed and case-insensitive.
    private getDashedLabels(): Set<string> {
        const dashedLines = this.props.variables?.dashedLines;
        if (!dashedLines || !dashedLines.trim()) {
            return new Set();
        }

        return new Set(dashedLines.split(',')
            .map((label) => label.trim().toLowerCase())
            .filter((label) => label.length > 0));
    }

    private buildChartData(normalized: NormalizedData): ChartData<"line", number[], string> {
        const colors = this.getColors();
        const dashedLabels = this.getDashedLabels();

        return {
            labels: normalized.labels,
            datasets: normalized.series.map((series, index) => {
                const color = colors[index % colors.length];
                // Single-line charts fill by default; multi-line only when a series opts in.
                const fill = series.fill ?? !normalized.isMultiLine;
                const isDashed = dashedLabels.has(series.label.trim().toLowerCase());

                return {
                    label: series.label,
                    data: series.values.map((point) => point.value),
                    fill,
                    borderColor: color,
                    pointBackgroundColor: color,
                    borderDash: isDashed ? [6, 6] : undefined,
                    tension: 0.4,
                    backgroundColor: !fill
                        ? undefined
                        : (context: any) => {
                            try {
                                const ctx = context.chart.ctx;
                                const gradient = ctx.createLinearGradient(0, 0, 0, 125);
                                gradient.addColorStop(0, "rgba(85, 66, 246, 0.3)");
                                gradient.addColorStop(1, "rgba(85, 66, 246, 0)");
                                return gradient;
                            } catch (e) {
                                console.error("[LineChartWidget] gradient callback threw:", e);
                                return "rgba(85, 66, 246, 0.15)";
                            }
                        },
                };
            }),
        };
    }

    private buildTooltipLabel(normalized: NormalizedData, tooltipItem: TooltipItem<"line">): string {
        const suffix = this.props.config?.isPercentage ? "%" : "";
        const value = `${tooltipItem.formattedValue}${suffix}`;
        const series = normalized.series[tooltipItem.datasetIndex];
        const point = series?.values[tooltipItem.dataIndex];

        if (normalized.isMultiLine) {
            const seriesLabel = tooltipItem.dataset.label ?? series?.label ?? "";
            const pointTitle = point?.title ?? tooltipItem.label;
            return `${seriesLabel}: ${pointTitle}: ${value}`;
        }

        const title = point?.title || tooltipItem.label;
        return `${title}: ${value}`;
    }

    render() {
        const normalized = this.normalize();
        const chartData = this.buildChartData(normalized);
        const displayLegend = this.props.config?.displayLegends === undefined
            ? normalized.isMultiLine
            : this.props.config.displayLegends;

        const options: ChartOptions<"line"> = {
            layout: {
                padding: {
                    top: 40,
                    right: 25
                }
            },
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            hover: {
                mode: 'nearest',
                intersect: false
            },
            plugins: {
                legend: {
                    display: displayLegend,
                    position: "bottom",
                    labels: {
                        usePointStyle: true,
                        pointStyle: 'rectRounded',
                        boxWidth: 8,
                        boxHeight: 8,
                        padding: 10,
                    }
                },
                tooltip: {
                    mode: "index",
                    intersect: false,
                    backgroundColor: '#3F4692',
                    cornerRadius: 16,
                    padding: 8,
                    caretSize: 6,
                    caretPadding: 6,
                    yAlign: "bottom",
                    displayColors: normalized.isMultiLine,
                    callbacks: {
                        label: (tooltipItem) => this.buildTooltipLabel(normalized, tooltipItem),
                        title: () => "",
                    },
                },
            },
            scales: {
                y: {
                    grid: {
                        tickLength: 0,
                        borderDash: [5, 5],
                        color: "#EAECF1",
                    },
                    beginAtZero: true,
                    ticks: {
                        autoSkip: true,
                        maxTicksLimit: 5,
                        padding: 6,
                        font: {
                            size: 10,
                        }
                    }
                },
                x: {
                    grid: {
                        tickLength: 0,
                        color: "#EAECF1",
                    },
                    beginAtZero: true,
                    ticks: {
                        padding: 6,
                        font: {
                            size: 10,
                        }
                    },
                }
            }
        };

        return (
            <div className="LineChartWidget">
                <Line data={chartData} options={options} />
            </div>
        );
    }
}

export default LineChartWidget;
